import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/ui";
import { Brain, Stethoscope, FileText, DollarSign, Users, Activity, Pill, ClipboardList } from "lucide-react";
import { useLocation } from "wouter";
const providersImage = "/assets/website/images/Healthcare_providers_with_AI_2f93ce2c.png";

const providerFeatures = [
  {
    icon: Brain,
    title: "Clinical Decision Support",
    description: "AI-powered recommendations based on latest medical research and patient data"
  },
  {
    icon: Stethoscope,
    title: "Diagnostic Assistance", 
    description: "Advanced image analysis and symptom correlation for accurate diagnoses"
  },
  {
    icon: FileText,
    title: "Treatment Planning",
    description: "Personalized treatment recommendations based on patient history and outcomes"
  },
  {
    icon: DollarSign,
    title: "Revenue Cycle Management",
    description: "Intelligent automation driving faster reimbursements and zero revenue leakage"
  },
  {
    icon: Users,
    title: "Care Coordination",
    description: "Seamless communication and task management across care teams"
  },
  {
    icon: Activity,
    title: "Patient Monitoring",
    description: "Real-time alerts and automated health status updates"
  },
  {
    icon: Pill,
    title: "Medication Management",
    description: "Smart reminders and drug interaction checking"
  },
  {
    icon: ClipboardList,
    title: "Documentation",
    description: "Automated care notes and progress tracking"
  }
];

export default function Providers() {
  const [, setLocation] = useLocation();

  return (
    <div>
      <main className="py-20">
        {/* Hero Image Section */}
        <div className="relative h-80 mb-20 overflow-hidden">
          <img 
            src={providersImage} 
            alt="Healthcare Providers with AI"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/80 to-background/60" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-primary mb-6" data-testid="text-providers-title">
                For Healthcare Providers
              </h1>
              <p className="text-xl text-foreground max-w-3xl mx-auto" data-testid="text-providers-subtitle">
                Empowering healthcare providers with intelligent AI solutions that enhance clinical decision-making, 
                streamline workflows, and improve patient outcomes.
              </p>
            </div>
          </div>
        </div>
        
        <div className="container mx-auto max-w-7xl">

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 place-items-center justify-items-center">
            {providerFeatures.map((feature, index) => (
              <Card key={index} className="hover-elevate transition-all duration-300" data-testid={`card-feature-${index}`}>
                <CardHeader className="text-center pb-4">
                  <div className="mx-auto mb-4 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-lg font-semibold" data-testid={`text-feature-title-${index}`}>
                    {feature.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <CardDescription className="text-center text-sm leading-relaxed" data-testid={`text-feature-description-${index}`}>
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Call to Action */}
          <div className="text-center mt-16">
            <div className="bg-card p-8 rounded-lg border max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-foreground mb-4" data-testid="text-cta-title">
                Ready to Transform Your Practice?
              </h2>
              <p className="text-muted-foreground mb-6" data-testid="text-cta-description">
                Join thousands of healthcare providers who are already leveraging AI to improve patient care and reduce administrative burden.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={() => setLocation('/book-demo')}
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-md font-medium hover-elevate" 
                  data-testid="button-schedule-demo"
                >
                  Book Live Demo
                </button>
                <button className="px-6 py-3 border border-input bg-background hover-elevate text-foreground rounded-md font-medium" data-testid="button-learn-more">
                  Learn More
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}